import { useEffect, useMemo, useState } from 'react'
import { Search, Save, X } from 'lucide-react'
import { clsx } from 'clsx'

/** Validate an ISIN check digit using the ISO 6166 Luhn-mod-10 algorithm.
 *  Returns true for valid 12-char ISINs and also for empty string so we
 *  don't block intermediate typing. */
function isValidIsin(raw: string): boolean {
  const s = raw.trim().toUpperCase()
  if (!s) return true
  if (!/^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(s)) return false
  // Expand letters to two digits (A=10, B=11, ...)
  let expanded = ''
  for (const ch of s) {
    expanded += /[A-Z]/.test(ch)
      ? String(ch.charCodeAt(0) - 55)
      : ch
  }
  // Luhn from right
  let sum = 0
  let doubleIt = false
  for (let i = expanded.length - 1; i >= 0; i--) {
    let n = Number(expanded[i])
    if (doubleIt) {
      n *= 2
      if (n > 9) n -= 9
    }
    sum += n
    doubleIt = !doubleIt
  }
  return sum % 10 === 0
}

/**
 * Bulk symbol / ISIN editor for holdings that can't be priced.
 *
 * Flow:
 *  • GET /api/accounts/stuck-holdings for the list
 *  • Each row is an inline form with symbol + ISIN inputs
 *  • Filter by account or by free-text fund name
 *  • Save batches all changes in one POST to /bulk-identifiers
 *
 * UI-only — doesn't mark-to-market. User runs "Refresh prices" on the
 * main accounts page after saving to pull live values for the newly
 * identifiable funds.
 */

interface StuckRow {
  holding_id: number
  account_id: number
  account_name: string
  name: string
  asset_type: string
  quantity: number
  value_gbp: number
  suggested_isin?: string | null
}

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  symbol: string
  isin: string
}

export function IdentifierEditor({ open, onClose, onSaved }: Props) {
  const [rows, setRows] = useState<StuckRow[]>([])
  const [drafts, setDrafts] = useState<Record<number, Draft>>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState('')
  const [accountFilter, setAccountFilter] = useState<string>('all')
  const [msg, setMsg] = useState<string | null>(null)
  const [searchingFor, setSearchingFor] = useState<number | null>(null)
  const [searchResults, setSearchResults] = useState<
    Record<number, Array<{ name: string; isin: string }>>
  >({})

  const token = localStorage.getItem('av-access-token') || ''

  const handleFtSearch = async (row: StuckRow) => {
    setSearchingFor(row.holding_id)
    try {
      const res = await fetch(
        `/api/accounts/search-fund-isin?q=${encodeURIComponent(row.name)}&limit=5`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) throw new Error(res.statusText)
      const body = await res.json()
      setSearchResults(prev => ({ ...prev, [row.holding_id]: body.candidates ?? [] }))
      if ((body.candidates ?? []).length === 0) {
        setMsg(`FT search found no matches for "${row.name}" — try typing the ISIN manually.`)
      }
    } catch (e: any) {
      setMsg(`FT search failed: ${e?.message ?? e}`)
    } finally {
      setSearchingFor(null)
    }
  }

  // Load on open + pre-fill suggested ISINs
  useEffect(() => {
    if (!open) return
    setLoading(true)
    setMsg(null)
    ;(async () => {
      try {
        const res = await fetch('/api/accounts/stuck-holdings', {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error(res.statusText)
        const body = await res.json()
        const loadedRows: StuckRow[] = body.holdings ?? []
        setRows(loadedRows)
        // Pre-fill ISIN drafts from the curated map so Simon only has
        // to review / override, not retype. Leave symbol blank — those
        // are per-listing (LSE vs US) so we don't auto-suggest.
        const prefill: Record<number, Draft> = {}
        let hinted = 0
        for (const r of loadedRows) {
          if (r.suggested_isin) {
            prefill[r.holding_id] = { symbol: '', isin: r.suggested_isin }
            hinted++
          }
        }
        setDrafts(prefill)
        if (hinted > 0) {
          setMsg(
            `Pre-filled ${hinted} ISINs from the curated UK fund map. ` +
            `Review, correct any wrong class (Acc vs Inc), then Save.`
          )
        }
      } catch (e: any) {
        setMsg(`Failed to load: ${e?.message ?? e}`)
      } finally {
        setLoading(false)
      }
    })()
  }, [open, token])

  const accounts = useMemo(() => {
    const seen = new Set<string>()
    for (const r of rows) seen.add(r.account_name)
    return Array.from(seen).sort()
  }, [rows])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rows.filter(r => {
      if (accountFilter !== 'all' && r.account_name !== accountFilter) return false
      if (!q) return true
      return r.name.toLowerCase().includes(q) || r.account_name.toLowerCase().includes(q)
    })
  }, [rows, query, accountFilter])

  const dirtyCount = Object.values(drafts).filter(d => (d.symbol || '').trim() || (d.isin || '').trim()).length

  const setDraft = (id: number, patch: Partial<Draft>) => {
    setDrafts(prev => {
      const current = prev[id] ?? { symbol: '', isin: '' }
      return { ...prev, [id]: { ...current, ...patch } }
    })
  }

  /** Fill an ISIN from one row into every other row with the same name.
   *  Common case: Simon types ISIN for "Fidelity Index World P Dis" on one
   *  row, same fund appears in 3 accounts — Ctrl+click or button pastes
   *  the same ISIN into all siblings so he doesn't type it 3×. */
  const applyToSameName = (sourceRow: StuckRow) => {
    const d = drafts[sourceRow.holding_id]
    if (!d) return
    const isin = (d.isin || '').trim()
    const symbol = (d.symbol || '').trim()
    if (!isin && !symbol) return
    const siblings = rows.filter(r =>
      r.holding_id !== sourceRow.holding_id && r.name === sourceRow.name
    )
    if (siblings.length === 0) return
    setDrafts(prev => {
      const next = { ...prev }
      for (const s of siblings) {
        next[s.holding_id] = {
          symbol: symbol || next[s.holding_id]?.symbol || '',
          isin:   isin   || next[s.holding_id]?.isin   || '',
        }
      }
      return next
    })
    setMsg(`Applied to ${siblings.length} other holding(s) with the same name.`)
  }

  const handleSave = async () => {
    const updates = Object.entries(drafts)
      .filter(([_, d]) => (d.symbol || '').trim() || (d.isin || '').trim())
      .map(([id, d]) => ({
        holding_id: Number(id),
        symbol: (d.symbol || '').trim() || undefined,
        isin: (d.isin || '').trim() || undefined,
      }))
    if (updates.length === 0) {
      setMsg('Nothing to save — fill in a symbol or ISIN first.')
      return
    }
    const invalidIsins = updates.filter(u => u.isin && !isValidIsin(u.isin))
    if (invalidIsins.length > 0) {
      setMsg(
        `${invalidIsins.length} ISIN(s) have invalid checksums — double-check before saving. ` +
        `Valid ISINs have 12 chars ending in a check digit.`
      )
      return
    }
    setSaving(true)
    setMsg(null)
    try {
      const res = await fetch('/api/accounts/bulk-identifiers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ updates }),
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        throw new Error(d?.detail ?? res.statusText)
      }
      const body = await res.json()
      const rp = body.reprice
      let msg = `Saved ${body.updated} holdings. `
      if (body.skipped) msg += `${body.skipped} skipped. `
      if (rp?.repriced != null) {
        const delta = Number(rp.delta_gbp ?? 0)
        const sign = delta >= 0 ? '+' : ''
        msg += `Reprice: ${rp.repriced} updated, Δ ${sign}£${Math.abs(delta).toLocaleString()}.`
      } else {
        msg += 'Close and click "Refresh prices" to mark-to-market.'
      }
      setMsg(msg)
      // Remove saved rows from the list + clear drafts for them
      const savedIds = new Set<number>(body.updated_holdings.map((h: { holding_id: number }) => h.holding_id))
      setRows(prev => prev.filter(r => !savedIds.has(r.holding_id)))
      setDrafts(prev => {
        const next = { ...prev }
        for (const id of savedIds) delete next[id]
        return next
      })
      onSaved()
    } catch (e: any) {
      setMsg(`Save failed: ${e?.message ?? e}`)
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-10 pb-10 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Bulk identifier editor"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-6xl mx-4 bg-surface-card border border-border rounded-lg shadow-2xl flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Tickers &amp; ISINs</h2>
            <p className="text-xs text-text-muted mt-0.5">
              Add a symbol or ISIN for any of these holdings and the reprice
              task will pick them up automatically. Symbol for Yahoo / MSN listings
              (NVDA, VUAG.L, BTC-USD), ISIN for UK unit trusts.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-border bg-surface-hover/30">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search size={14} className="text-text-muted shrink-0" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Filter by fund name or account…"
              aria-label="Filter holdings"
              className="flex-1 bg-transparent outline-none text-sm text-text-primary placeholder:text-text-muted"
            />
          </div>
          <select
            value={accountFilter}
            onChange={e => setAccountFilter(e.target.value)}
            aria-label="Filter by account"
            className="bg-surface-card border border-border rounded px-2 py-1 text-sm text-text-primary"
          >
            <option value="all">All accounts ({rows.length})</option>
            {accounts.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <span className="text-xs text-text-muted">
            {filtered.length} shown · {dirtyCount} ready to save
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || dirtyCount === 0}
            className="btn-primary gap-2"
          >
            <Save size={14} aria-hidden />
            {saving ? 'Saving…' : `Save ${dirtyCount || ''}`}
          </button>
        </div>

        {msg && (
          <div className="px-4 py-2 text-xs text-text-primary bg-blue/10 border-b border-blue/30">
            {msg}
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-text-muted text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-text-muted text-sm">
              {rows.length === 0 ? 'No stuck holdings 🎉' : 'No matches for that filter'}
            </div>
          ) : (
            <div className="table-responsive">
            <table className="w-full text-sm">
              <thead className="text-xs text-text-muted uppercase tracking-wider sticky top-0 bg-surface-card">
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 font-medium">Account</th>
                  <th className="text-left px-4 py-2 font-medium">Holding</th>
                  <th className="text-right px-4 py-2 font-medium">Qty</th>
                  <th className="text-right px-4 py-2 font-medium">Value</th>
                  <th className="text-left px-4 py-2 font-medium w-32">Symbol</th>
                  <th className="text-left px-4 py-2 font-medium w-40">ISIN</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const d = drafts[r.holding_id] ?? { symbol: '', isin: '' }
                  const hasValue = (d.symbol || '').trim() || (d.isin || '').trim()
                  const isSuggested = r.suggested_isin && d.isin === r.suggested_isin
                  const siblingCount = rows.filter(
                    x => x.holding_id !== r.holding_id && x.name === r.name
                  ).length
                  return (
                    <tr key={r.holding_id} className={clsx(
                      'border-b border-border/50 hover:bg-surface-hover/30 transition-colors',
                      hasValue && !isSuggested && 'bg-emerald/5',
                      isSuggested && 'bg-blue/5',
                    )}>
                      <td className="px-4 py-2 text-text-secondary truncate max-w-[200px]">{r.account_name}</td>
                      <td className="px-4 py-2 text-text-primary max-w-[260px]" title={r.name}>
                        <div className="flex items-center gap-2">
                          <span className="truncate">{r.name}</span>
                          {isSuggested && (
                            <span className="text-[10px] text-blue-light uppercase tracking-wider shrink-0">
                              suggested
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleFtSearch(r)}
                            disabled={searchingFor === r.holding_id}
                            title="Search Financial Times for candidate ISINs matching this fund name"
                            aria-label="Search FT for ISIN"
                            className="shrink-0 text-[10px] text-text-muted hover:text-blue-light underline disabled:opacity-40"
                          >
                            {searchingFor === r.holding_id ? '…' : 'FT'}
                          </button>
                        </div>
                        {searchResults[r.holding_id] && searchResults[r.holding_id].length > 0 && (
                          <div className="mt-1 border border-blue/30 bg-blue/5 rounded text-xs">
                            {searchResults[r.holding_id].map(c => (
                              <button
                                key={c.isin}
                                type="button"
                                onClick={() => {
                                  setDraft(r.holding_id, { isin: c.isin })
                                  setSearchResults(prev => {
                                    const next = { ...prev }
                                    delete next[r.holding_id]
                                    return next
                                  })
                                }}
                                className="w-full text-left px-2 py-1 hover:bg-blue/10 border-b border-blue/10 last:border-b-0 flex items-center gap-2"
                              >
                                <span className="font-mono text-blue-light">{c.isin}</span>
                                <span className="truncate text-text-secondary">{c.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-text-secondary text-xs">
                        {(r.quantity ?? 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-text-primary">
                        £{Math.round(r.value_gbp).toLocaleString()}
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={d.symbol}
                          onChange={e => setDraft(r.holding_id, { symbol: e.target.value })}
                          placeholder="e.g. VUAG.L"
                          aria-label={`Symbol for ${r.name}`}
                          className="w-full bg-surface-card border border-border rounded px-2 py-1 text-sm font-mono outline-none focus:border-blue text-text-primary"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={d.isin}
                            onChange={e => setDraft(r.holding_id, { isin: e.target.value.toUpperCase() })}
                            placeholder="e.g. GB00BJS8SJ34"
                            aria-label={`ISIN for ${r.name}`}
                            title={
                              !isValidIsin(d.isin)
                                ? 'Invalid ISIN checksum — check you copied all 12 characters'
                                : undefined
                            }
                            maxLength={12}
                            className={clsx(
                              'flex-1 bg-surface-card border rounded px-2 py-1 text-sm font-mono outline-none text-text-primary',
                              isValidIsin(d.isin)
                                ? 'border-border focus:border-blue'
                                : 'border-rose/60 focus:border-rose',
                            )}
                          />
                          {siblingCount > 0 && hasValue && (
                            <button
                              type="button"
                              onClick={() => applyToSameName(r)}
                              title={`Apply this ISIN/symbol to the ${siblingCount} other holding(s) with the same name`}
                              aria-label={`Apply to ${siblingCount} siblings`}
                              className="shrink-0 text-[10px] px-1.5 py-1 rounded bg-blue/20 hover:bg-blue/30 text-blue-light font-medium"
                            >
                              →{siblingCount}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-3 border-t border-border bg-surface-hover/30 text-xs text-text-muted">
          <span>
            Tip: ISINs can be found on FT, trustnet, or the fund factsheet.
            Enter only one of symbol/ISIN per row — whichever the source supports.
          </span>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary text-xs"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
